import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import { parseAmericanDate } from '@/utils/date-util'
import {
  mustBeInThePast,
  mustBeValidPersonalTaxpayerIdentificationNumber,
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
} from '@/utils/validx-util'
import { formatProfileDate } from '@/features/jobs/info-gathering/info-gathering-formatters'
import type { PersonalInfo } from './PersonalInfo'

/**
 * View model for editing personal information.
 */
export class PersonalInfoEditorViewModel {
  @observable private accessor _firstName = ''
  @observable private accessor _middleInitial = ''
  @observable private accessor _lastName = ''
  @observable private accessor _dateOfBirth = ''
  @observable private accessor _personalTaxpayerIdentificationNumber = ''

  readonly validation = validationContext(this, {
    firstName: [
      required('First name is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'First name must not exceed 100 characters'),
    ],
    middleInitial: [
      mustHaveMaxLength(1, 'Middle initial must be a single character'),
      mustBeValidTaxFormInputValue(),
    ],
    lastName: [
      required('Last name is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Last name must not exceed 100 characters'),
    ],
    dateOfBirth: [
      required('Date of birth is required'),
      mustBeInThePast('Date of birth must be in the past'),
    ],
    personalTaxpayerIdentificationNumber: [
      required('SSN/ITIN is required'),
      mustBeValidPersonalTaxpayerIdentificationNumber(),
    ],
  })

  @computed get firstName() {
    return this._firstName
  }

  set firstName(value: string) {
    this._firstName = value
    this.validation.clearErrors('firstName')
  }

  @computed get middleInitial() {
    return this._middleInitial
  }

  set middleInitial(value: string) {
    this._middleInitial = value
    this.validation.clearErrors('middleInitial')
  }

  @computed get lastName() {
    return this._lastName
  }

  set lastName(value: string) {
    this._lastName = value
    this.validation.clearErrors('lastName')
  }

  /**
   * The date of birth as typed, in `MM/dd/yyyy`.
   */
  @computed get dateOfBirth() {
    return this._dateOfBirth
  }

  set dateOfBirth(value: string) {
    this._dateOfBirth = value
    this.validation.clearErrors('dateOfBirth')
  }

  @computed get personalTaxpayerIdentificationNumber() {
    return this._personalTaxpayerIdentificationNumber
  }

  set personalTaxpayerIdentificationNumber(value: string) {
    this._personalTaxpayerIdentificationNumber = value
    this.validation.clearErrors('personalTaxpayerIdentificationNumber')
  }

  /**
   * Resets the state of the view model to that of the given info, or
   * defaults if none is specified.
   */
  @action.bound
  reset(info?: PersonalInfo) {
    this.firstName = info?.firstName ?? ''
    this.middleInitial = info?.middleInitial ?? ''
    this.lastName = info?.lastName ?? ''
    this.dateOfBirth = formatProfileDate(info?.dateOfBirth ?? null)
    this.personalTaxpayerIdentificationNumber =
      info?.personalTaxpayerIdentificationNumber ?? ''
    this.validation.reset()
  }

  /**
   * Maps the state of the VM to {@link PersonalInfo}.
   */
  toPersonalInfo(): PersonalInfo {
    return {
      firstName: this.firstName,
      middleInitial: this.middleInitial || null,
      lastName: this.lastName,
      dateOfBirth: parseAmericanDate(this.dateOfBirth),
      personalTaxpayerIdentificationNumber:
        this.personalTaxpayerIdentificationNumber,
    }
  }
}
